import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@marine/api-client/api';

/**
 * Platform per-tenant AI Prompts + AI Configuration (#1029) — the
 * platform-admin replacement for the deleted dealer-facing tabs. Mirrors
 * `services/aiSettingsApi.ts` (same directory) for shape and
 * `platformAiPromptsApi.ts` for the prompt field set — field names are
 * hand-declared here (not pulled from `@marine/contracts`) for the same
 * reason those two sibling services do: the generated `variables` shape
 * (`{name, description}[]`) does not match the runtime shape
 * (`string[] | null`) either editor actually renders, a pre-existing
 * openapi/runtime drift this plan does not touch.
 */
export interface TenantAIPrompt {
  /** Null when the tenant has not customized this prompt — the effective platform default is returned. */
  id: string | null;
  module: string;
  key: string;
  name: string;
  system_prompt: string;
  user_prompt_template: string;
  model_override: string | null;
  temperature_override: number | null;
  max_tokens_override: number | null;
  is_active: boolean;
  sort_order: number;
  description: string | null;
  variables: string[] | null;
  metadata: { category?: string; [key: string]: unknown } | null;
  /** True when this is the tenant's own row; false when it is the live platform default. */
  is_customized: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface UpsertTenantAIPromptInput {
  system_prompt?: string;
  user_prompt_template?: string;
  model_override?: string | null;
  temperature_override?: number | null;
  max_tokens_override?: number | null;
  is_active?: boolean;
}

export interface TenantAiConfig {
  tenant_id: string;
  ai_enabled: boolean;
  openrouter_model: string | null;
  perplexity_model: string | null;
  ai_temperature: number | null;
  ai_max_tokens: number | null;
  vision_model: string | null;
  specs_model: string | null;
  description_model: string | null;
  pricing_model: string | null;
  daily_ai_limit: number | null;
  monthly_ai_limit: number | null;
  banned_words: string | null;
  include_location_in_descriptions: boolean;
  auto_analyze_photos: boolean;
  updated_at: string | null;
}

export interface UpdateTenantAiConfigInput {
  ai_enabled?: boolean;
  openrouter_model?: string | null;
  perplexity_model?: string | null;
  ai_temperature?: number | null;
  ai_max_tokens?: number | null;
  vision_model?: string | null;
  specs_model?: string | null;
  description_model?: string | null;
  pricing_model?: string | null;
  daily_ai_limit?: number | null;
  monthly_ai_limit?: number | null;
  banned_words?: string | null;
  include_location_in_descriptions?: boolean;
  auto_analyze_photos?: boolean;
}

/**
 * The DELETE (revert) endpoint returns `{data: TenantAIPrompt, message}`,
 * not the bare `{message}` shape `api.deleteOne()` assumes — unwrap by hand.
 */
async function deleteReturningData<T>(url: string): Promise<T> {
  const response = await api.delete<{ data: T; message: string }>(url);
  return response.data.data;
}

export const tenantAiAdminApi = {
  listPrompts: (tenantId: string, module?: string): Promise<TenantAIPrompt[]> => {
    const params = module ? `?module=${module}` : '';

    return api.getList<TenantAIPrompt>(`/api/tenants/${tenantId}/ai-prompts${params}`);
  },
  upsertPrompt: (
    tenantId: string,
    module: string,
    key: string,
    input: UpsertTenantAIPromptInput,
  ): Promise<TenantAIPrompt> =>
    api.putOne<TenantAIPrompt>(`/api/tenants/${tenantId}/ai-prompts/key/${module}/${key}`, input),
  revertPrompt: (tenantId: string, module: string, key: string): Promise<TenantAIPrompt> =>
    deleteReturningData<TenantAIPrompt>(`/api/tenants/${tenantId}/ai-prompts/key/${module}/${key}`),

  showConfig: (tenantId: string): Promise<TenantAiConfig> =>
    api.getOne<TenantAiConfig>(`/api/tenants/${tenantId}/ai-config`),
  updateConfig: (tenantId: string, input: UpdateTenantAiConfigInput): Promise<TenantAiConfig> =>
    api.putOne<TenantAiConfig>(`/api/tenants/${tenantId}/ai-config`, input),
};

export function useTenantAiPrompts(tenantId: string | undefined, module?: string) {
  return useQuery({
    queryKey: ['platform-tenant-ai-prompts', tenantId, module],
    queryFn: () => tenantAiAdminApi.listPrompts(tenantId!, module),
    enabled: !!tenantId,
  });
}

export function useCustomizeTenantAiPrompt(tenantId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      module,
      key,
      input,
    }: {
      module: string;
      key: string;
      input: UpsertTenantAIPromptInput;
    }) => tenantAiAdminApi.upsertPrompt(tenantId, module, key, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-tenant-ai-prompts', tenantId] });
    },
  });
}

export function useRevertTenantAiPrompt(tenantId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ module, key }: { module: string; key: string }) =>
      tenantAiAdminApi.revertPrompt(tenantId, module, key),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-tenant-ai-prompts', tenantId] });
    },
  });
}

export function useTenantAiConfig(tenantId: string | undefined) {
  return useQuery({
    queryKey: ['platform-tenant-ai-config', tenantId],
    queryFn: () => tenantAiAdminApi.showConfig(tenantId!),
    enabled: !!tenantId,
  });
}

export function useUpdateTenantAiConfig(tenantId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateTenantAiConfigInput) => tenantAiAdminApi.updateConfig(tenantId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-tenant-ai-config', tenantId] });
    },
  });
}
